import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, Save, ChevronDown, ChevronRight, Clock, AlertTriangle, CheckCircle2, CreditCard, Sparkles } from 'lucide-react'
import { useSystemSettings, type SystemSetting } from '@/hooks/useSystemSettings'
import { toast } from '@/hooks/use-toast'
import { invokeEdgeFunction } from '@/lib/edge-functions'

const SETTING_LABELS: Record<string, { label: string; description: string; type: 'number' | 'text' | 'boolean'; showWhen?: string }> = {
  max_managers_per_league: {
    label: 'Max Managers Per League',
    description: 'Maximum number of members allowed per league',
    type: 'number',
  },
  payment_required: {
    label: 'Payment Required',
    description: 'Require Stripe payment when creating a league',
    type: 'boolean',
  },
  season_registration_fee: {
    label: 'Season Registration Fee (USD)',
    description: 'Amount charged to create a new league',
    type: 'number',
    showWhen: 'payment_required',
  },
  ai_summaries_enabled: {
    label: 'AI Summaries',
    description: 'Generate AI-powered season wrap-ups and matchday recaps',
    type: 'boolean',
  },
}

interface StripeKeyStatus { hasSecretKey: boolean; hasWebhookSecret: boolean }
interface StripeStatusResponse {
  mode: 'test' | 'live'
  activeKeys: StripeKeyStatus
  keys: { test: StripeKeyStatus; live: StripeKeyStatus }
}

function StripeStatusCard() {
  const { updateSetting } = useSystemSettings()
  const queryClient = useQueryClient()
  const [switching, setSwitching] = useState(false)

  const { data: stripeStatus, isLoading, isError } = useQuery({
    queryKey: ['stripe-status'],
    queryFn: async () => {
      const { data, error } = await invokeEdgeFunction<StripeStatusResponse>('check-stripe-status')
      if (error) throw new Error(error.message)
      return data!
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  async function toggleMode() {
    if (!stripeStatus) return
    const newMode = stripeStatus.mode === 'test' ? 'live' : 'test'
    const targetKeys = stripeStatus.keys[newMode]

    if (!targetKeys.hasSecretKey) {
      toast({
        title: `Cannot switch to ${newMode} mode`,
        description: `No ${newMode} Stripe secret key is configured. Contact your developer to set STRIPE_SECRET_KEY_${newMode.toUpperCase()}.`,
        variant: 'destructive',
      })
      return
    }

    setSwitching(true)
    try {
      await updateSetting.mutateAsync({ key: 'stripe_mode', value: newMode })
      queryClient.invalidateQueries({ queryKey: ['stripe-status'] })
      toast({ title: 'Stripe mode updated', description: `Now using ${newMode} mode` })
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to switch mode'
      toast({ title: 'Failed to switch mode', description: msg, variant: 'destructive' })
    } finally {
      setSwitching(false)
    }
  }

  if (isLoading) {
    return (
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 px-5 py-4">
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
          <span className="text-sm text-zinc-500">Checking Stripe configuration...</span>
        </div>
      </div>
    )
  }

  if (isError || !stripeStatus) {
    return (
      <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30 px-5 py-4">
        <div className="flex items-center gap-2.5">
          <AlertTriangle className="h-4 w-4 text-red-500" />
          <p className="text-sm text-red-700 dark:text-red-400">Unable to check Stripe configuration</p>
        </div>
      </div>
    )
  }

  const { mode, activeKeys, keys } = stripeStatus
  const missingActiveKeys = !activeKeys.hasSecretKey || !activeKeys.hasWebhookSecret

  return (
    <div className={`rounded-xl border px-5 py-4 space-y-3 ${
      missingActiveKeys
        ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30'
        : mode === 'test'
        ? 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30'
        : 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30'
    }`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <CreditCard className="h-4 w-4 text-zinc-500" />
          <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Stripe Configuration</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-medium ${mode === 'live' ? 'text-emerald-600' : 'text-amber-600'}`}>
            {mode === 'live' ? 'Live' : 'Test'}
          </span>
          <button
            onClick={toggleMode}
            disabled={switching || updateSetting.isPending}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40
                        disabled:opacity-50
                        ${mode === 'live' ? 'bg-emerald-600' : 'bg-amber-500'}`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-sm ring-0 transition-transform
                          ${mode === 'live' ? 'translate-x-5' : 'translate-x-0'}`}
            />
          </button>
        </div>
      </div>
      <div className="flex items-center gap-4 text-xs">
        <span className="flex items-center gap-1">
          {activeKeys.hasSecretKey ? <CheckCircle2 className="h-3 w-3 text-emerald-500" /> : <AlertTriangle className="h-3 w-3 text-red-500" />}
          Secret Key
        </span>
        <span className="flex items-center gap-1">
          {activeKeys.hasWebhookSecret ? <CheckCircle2 className="h-3 w-3 text-emerald-500" /> : <AlertTriangle className="h-3 w-3 text-red-500" />}
          Webhook Secret
        </span>
      </div>
      {!activeKeys.hasSecretKey && (
        <p className="text-xs text-red-600 dark:text-red-400">
          Checkout will fail — no {mode} secret key is configured. Set STRIPE_SECRET_KEY_{mode.toUpperCase()} in Supabase secrets.
        </p>
      )}
      {!activeKeys.hasWebhookSecret && activeKeys.hasSecretKey && (
        <p className="text-xs text-amber-600 dark:text-amber-400">
          Webhook verification disabled — no {mode} webhook secret configured. Set STRIPE_WEBHOOK_SECRET_{mode.toUpperCase()} in Supabase secrets.
        </p>
      )}
      {mode === 'test' && activeKeys.hasSecretKey && (
        <p className="text-xs text-amber-600 dark:text-amber-400">
          Test mode — no real charges will be made.
        </p>
      )}
    </div>
  )
}

interface ProviderConfig {
  id: string
  provider: string
  key_hint: string
  model: string
  is_active: boolean
  updated_at: string
}

interface ManageAIResponse {
  providers: ProviderConfig[]
  models: Record<string, string[]>
  hasEncryptionKey: boolean
}

function AIProviderCard() {
  const queryClient = useQueryClient()
  const [provider, setProvider] = useState<'anthropic' | 'openai'>('anthropic')
  const [apiKey, setApiKey] = useState('')
  const [model, setModel] = useState('')
  const [saving, setSaving] = useState(false)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['ai-providers'],
    queryFn: async () => {
      const { data, error } = await invokeEdgeFunction<ManageAIResponse>('manage-ai-provider')
      if (error) throw new Error(error.message)
      return data!
    },
    staleTime: 30 * 1000,
  })

  const availableModels = data?.models?.[provider] ?? []
  const activeConfig = data?.providers?.find(p => p.is_active)
  const providerConfig = data?.providers?.find(p => p.provider === provider)

  async function handleSave() {
    if (!model) {
      toast({ title: 'Missing fields', description: 'Model is required', variant: 'destructive' })
      return
    }
    if (!apiKey && !providerConfig) {
      toast({ title: 'Missing fields', description: 'API key is required for new provider', variant: 'destructive' })
      return
    }
    setSaving(true)
    try {
      const { error } = await invokeEdgeFunction('manage-ai-provider', {
        body: { provider, ...(apiKey && { apiKey }), model, isActive: true },
      })
      if (error) throw new Error(error.message)
      setApiKey('')
      queryClient.invalidateQueries({ queryKey: ['ai-providers'] })
      queryClient.invalidateQueries({ queryKey: ['ai-status'] })
      toast({ title: 'AI provider saved', description: `${provider} configured with ${model}` })
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to save'
      toast({ title: 'Error', description: msg, variant: 'destructive' })
    } finally {
      setSaving(false)
    }
  }

  const inputClass = 'w-full px-3 py-2 text-sm rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/40'

  return (
    <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 overflow-hidden">
      <div className="px-5 py-4 border-b border-zinc-100 dark:border-zinc-800">
        <div className="flex items-center gap-2.5">
          <Sparkles className="h-4 w-4 text-zinc-500" />
          <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">AI Provider</p>
        </div>
        <p className="text-xs text-zinc-500 mt-1">Configure which AI provider and model to use for generating summaries.</p>
      </div>

      {isLoading ? (
        <div className="px-5 py-6 flex justify-center">
          <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
        </div>
      ) : isError ? (
        <div className="px-5 py-4">
          <div className="flex items-center gap-2.5">
            <AlertTriangle className="h-4 w-4 text-red-500" />
            <p className="text-sm text-red-700 dark:text-red-400">Failed to load AI provider configuration</p>
          </div>
        </div>
      ) : (
        <>
          {/* Current status — check encryption key first */}
          {!data?.hasEncryptionKey ? (
            <div className="px-5 py-3 border-b border-zinc-100 dark:border-zinc-800 bg-amber-50 dark:bg-amber-950/20">
              <div className="flex items-center gap-2 text-xs">
                <AlertTriangle className="h-3 w-3 text-amber-500" />
                <span className="text-amber-700 dark:text-amber-400">
                  Set AI_ENCRYPTION_KEY in Supabase secrets before adding provider keys
                </span>
              </div>
            </div>
          ) : activeConfig ? (
            <div className="px-5 py-3 border-b border-zinc-100 dark:border-zinc-800 bg-emerald-50 dark:bg-emerald-950/20">
              <div className="flex items-center gap-2 text-xs">
                <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                <span className="text-emerald-700 dark:text-emerald-400">
                  Active: {activeConfig.provider} / {activeConfig.model} (key: {activeConfig.key_hint})
                </span>
              </div>
            </div>
          ) : (
            <div className="px-5 py-3 border-b border-zinc-100 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-800/50">
              <p className="text-xs text-zinc-500">No AI provider configured yet</p>
            </div>
          )}

          {/* Config form */}
          <div className="px-5 py-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs text-zinc-500 font-medium">Provider</label>
                <select
                  value={provider}
                  onChange={(e) => {
                    const p = e.target.value as 'anthropic' | 'openai'
                    setProvider(p)
                    const existing = data?.providers?.find(c => c.provider === p)
                    setModel(existing?.model ?? '')
                  }}
                  className={inputClass}
                >
                  <option value="anthropic">Anthropic</option>
                  <option value="openai">OpenAI</option>
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs text-zinc-500 font-medium">Model</label>
                <select
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  className={inputClass}
                >
                  <option value="">Select model...</option>
                  {availableModels.map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-zinc-500 font-medium">
                API Key
                {providerConfig && (
                  <span className="text-zinc-400 font-normal ml-1">(current: {providerConfig.key_hint})</span>
                )}
              </label>
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={providerConfig ? 'Enter new key to update...' : `${provider === 'anthropic' ? 'sk-ant-...' : 'sk-...'}`}
                className={inputClass}
              />
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleSave}
                disabled={saving || (!apiKey && !providerConfig) || !model || !data?.hasEncryptionKey}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg
                           bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                Save & Activate
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default function AdminPlatformSettings() {
  const { settings, isLoading, updateSetting, getSettingHistory } = useSystemSettings()
  const [editValues, setEditValues] = useState<Record<string, string>>({})
  const [expandedKey, setExpandedKey] = useState<string | null>(null)

  function handleEdit(key: string, currentValue: unknown) {
    if (!(key in editValues)) {
      setEditValues(prev => ({ ...prev, [key]: String(currentValue ?? '') }))
    }
  }

  async function handleSave(key: string) {
    const raw = editValues[key]
    if (raw === undefined) return

    const meta = SETTING_LABELS[key]
    const value = meta?.type === 'number' ? Number(raw) : raw

    if (meta?.type === 'number' && (isNaN(value as number) || (value as number) < 0)) {
      toast({ title: 'Invalid value', description: 'Must be a non-negative number', variant: 'destructive' })
      return
    }

    try {
      await updateSetting.mutateAsync({ key, value })
      setEditValues(prev => {
        const next = { ...prev }
        delete next[key]
        return next
      })
      toast({ title: 'Setting updated', description: `${meta?.label ?? key} set to ${value}` })
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to save'
      toast({ title: 'Save failed', description: msg, variant: 'destructive' })
    }
  }

  const paymentSetting = settings.find(s => s.key === 'payment_required')
  const paymentEnabled = paymentSetting?.value === true || paymentSetting?.value === 'true'
  const aiSetting = settings.find(s => s.key === 'ai_summaries_enabled')
  const aiEnabled = aiSetting?.value === true || aiSetting?.value === 'true'

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">Platform Settings</h1>
        <p className="text-sm text-zinc-500 mt-1">Global defaults for the platform. Changes are tracked with full history.</p>
      </div>

      <div className="space-y-3">
        {/* Merge DB settings with any SETTING_LABELS keys not yet in DB */}
        {(() => {
          const dbKeys = new Set(settings.map(s => s.key))
          const missingKeys = Object.keys(SETTING_LABELS).filter(k => !dbKeys.has(k))
          const defaults: Record<string, unknown> = { payment_required: false, season_registration_fee: 20, max_managers_per_league: 20 }
          const missing = missingKeys.map(key => ({ key, value: defaults[key] ?? '', updated_by: null, created_at: new Date().toISOString() }))
          const byKey = new Map([...settings, ...missing].map(s => [s.key, s]))
          const currentValues = new Map([...settings, ...missing].map(s => [s.key, s.value]))
          const ordered = Object.keys(SETTING_LABELS).map(k => byKey.get(k)).filter(Boolean) as typeof settings
          return ordered.filter(s => {
            const dep = SETTING_LABELS[s.key]?.showWhen
            if (!dep) return true
            const depVal = currentValues.get(dep)
            return depVal === true || depVal === 'true'
          })
        })().map((setting) => {
          const meta = SETTING_LABELS[setting.key]
          const isEditing = setting.key in editValues
          const history = getSettingHistory(setting.key)
          const isExpanded = expandedKey === setting.key

          return (
            <div
              key={setting.key}
              className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900"
            >
              <div className="px-5 py-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                      {meta?.label ?? setting.key}
                    </p>
                    {meta?.description && (
                      <p className="text-xs text-zinc-500 mt-0.5">{meta.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {meta?.type === 'boolean' ? (
                      <button
                        onClick={async () => {
                          const current = setting.value === true || setting.value === 'true'
                          try {
                            await updateSetting.mutateAsync({ key: setting.key, value: !current })
                            toast({ title: 'Setting updated', description: `${meta.label} ${!current ? 'enabled' : 'disabled'}` })
                          } catch (e) {
                            const msg = e instanceof Error ? e.message : 'Failed to save'
                            toast({ title: 'Save failed', description: msg, variant: 'destructive' })
                          }
                        }}
                        disabled={updateSetting.isPending}
                        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors
                                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40
                                    disabled:opacity-50
                                    ${(setting.value === true || setting.value === 'true')
                                      ? 'bg-emerald-600'
                                      : 'bg-zinc-200 dark:bg-zinc-700'}`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-sm ring-0 transition-transform
                                      ${(setting.value === true || setting.value === 'true') ? 'translate-x-5' : 'translate-x-0'}`}
                        />
                      </button>
                    ) : isEditing ? (
                      <>
                        <input
                          type={meta?.type === 'number' ? 'number' : 'text'}
                          value={editValues[setting.key]}
                          onChange={(e) => setEditValues(prev => ({ ...prev, [setting.key]: e.target.value }))}
                          className="w-24 px-2.5 py-1.5 text-sm rounded-lg border border-zinc-200 dark:border-zinc-700
                                     bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100
                                     focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                          min={0}
                        />
                        <button
                          onClick={() => handleSave(setting.key)}
                          disabled={updateSetting.isPending}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg
                                     bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                        >
                          {updateSetting.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                          Save
                        </button>
                        <button
                          onClick={() => setEditValues(prev => {
                            const next = { ...prev }
                            delete next[setting.key]
                            return next
                          })}
                          className="text-xs text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="text-sm font-mono text-zinc-900 dark:text-zinc-100">
                          {String(setting.value)}
                        </span>
                        <button
                          onClick={() => handleEdit(setting.key, setting.value)}
                          className="text-xs text-emerald-600 dark:text-emerald-400 hover:underline"
                        >
                          Edit
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-2">
                  <p className="text-xs text-zinc-400">
                    Last updated {new Date(setting.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  </p>
                  {history.length > 1 && (
                    <button
                      onClick={() => setExpandedKey(isExpanded ? null : setting.key)}
                      className="flex items-center gap-0.5 text-xs text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
                    >
                      {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                      {history.length} changes
                    </button>
                  )}
                </div>
              </div>

              {isExpanded && history.length > 1 && (
                <div className="border-t border-zinc-100 dark:border-zinc-800 px-5 py-3 space-y-2">
                  <p className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Change History</p>
                  {history.map((h, i) => (
                    <div key={i} className="flex items-center gap-3 text-xs">
                      <Clock className="h-3 w-3 text-zinc-400 shrink-0" />
                      <span className="font-mono text-zinc-700 dark:text-zinc-300">{String(h.value)}</span>
                      <span className="text-zinc-400">
                        {new Date(h.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                      </span>
                      {i === 0 && <span className="text-emerald-600 dark:text-emerald-400 font-medium">current</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {paymentEnabled && <StripeStatusCard />}
      {aiEnabled && <AIProviderCard />}
    </div>
  )
}
